<script setup lang="ts">
/**
 * Lege toestand: er is (nog) niets om te tonen.
 *
 *   <VEmptyState                                     pagina (index zonder records)
 *       title="global.no_records_found"
 *       sub-title="global.not_yet_added"
 *       plural-model-type="invoice.invoices"
 *       singular-model-type="invoice.invoice"
 *       :can-create="can('invoices.create')"
 *       :create-route="route('invoices.create')"
 *       route-text="dashboard.click_to_add_new_item"
 *       :icon="IconClipboardText"
 *   />
 *   <VEmptyState variant="section" :icon="IconPaperclip" title="attachment.no_attachment_found" />
 *   <VEmptyState variant="row" :colspan="4" title="role.no_permissions_found" />
 *   <VEmptyState variant="inline" title="global.no_results_found" />   (in een dropdown)
 *
 * Opvolger van Components/EmptyState.vue (58 indexpagina's) én van de eigen kopieën in
 * het dashboard, de bijlagenmodals, het portaal en de dropdowns.
 *
 * - page: de bestaande look, met de grote marge bovenaan (enkel voor een lege index).
 *   Vroeger zat die `mt-64` er altijd in, ook in modals en tabs.
 * - section: compact, voor kaarten, tabs en modals.
 * - row: een tabelrij over alle kolommen (`colspan`).
 * - inline: één regel, voor zoekresultaten in een keuzelijst.
 *
 * Bewust geen HTML-commentaar tussen de varianten in de template: dat maakt de root een
 * fragment, waardoor klassen van de aanroeper niet meer op het element landen.
 *
 * Teksten zijn vertaalsleutels (`type` = vertaalde pluralModelType, zoals voorheen).
 * Een actie naast of in plaats van de link: slot #action.
 */
import { Link } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import { type Component, computed, useSlots } from 'vue'

const props = withDefaults(
    defineProps<{
        variant?: 'page' | 'section' | 'row' | 'inline'
        /** Vertaalsleutel. */
        title?: string
        /** Vertaalsleutel; weglaten = geen tweede regel. */
        subTitle?: string | null
        pluralModelType?: string | null
        singularModelType?: string | null
        canCreate?: boolean
        createRoute?: string | null
        /** Vertaalsleutel van de link naar createRoute. */
        routeText?: string | null
        icon?: Component | null
        /** Enkel bij variant row: aantal kolommen. */
        colspan?: number
        /** Parameters voor title/subTitle (naast `type`). */
        params?: Record<string, string | number>
    }>(),
    {
        variant: 'page',
        title: 'global.no_records_found',
        subTitle: null,
        pluralModelType: null,
        singularModelType: null,
        canCreate: false,
        createRoute: null,
        routeText: null,
        icon: null,
        colspan: 1,
        params: () => ({}),
    }
)

const slots = useSlots()

const textParams = computed(() => ({
    ...(props.pluralModelType ? { type: trans(props.pluralModelType) } : {}),
    ...props.params,
}))

/**
 * Bij de generieke titel "Geen records gevonden" liever de specifieke sleutel van het
 * model, bv. invoice.invoices → invoice.no_invoices_found, als die vertaling bestaat.
 */
const displayTitle = computed(() => {
    if (props.pluralModelType && props.title === 'global.no_records_found') {
        const parts = props.pluralModelType.split('.')
        parts[parts.length - 1] = `no_${parts[parts.length - 1]}_found`
        const specificKey = parts.join('.')
        const specific = trans(specificKey)
        if (specific && specific !== specificKey) {
            return specific
        }
    }
    return trans(props.title, textParams.value)
})

const displaySubTitle = computed(() =>
    props.subTitle ? trans(props.subTitle, textParams.value) : null
)

const createText = computed(() =>
    props.routeText
        ? trans(props.routeText, {
              type: props.singularModelType ? trans(props.singularModelType) : '',
          })
        : null
)

const showCreateLink = computed(() => props.canCreate && !!props.createRoute && !!createText.value)
const hasAction = computed(() => showCreateLink.value || !!slots.action)
</script>

<template>
    <tr v-if="variant === 'row'" class="v-empty-state">
        <td :colspan="colspan" class="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {{ displayTitle }}
        </td>
    </tr>

    <p
        v-else-if="variant === 'inline'"
        class="v-empty-state m-0 w-full cursor-default select-none px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
    >
        {{ displayTitle }}
    </p>

    <div
        v-else-if="variant === 'section'"
        class="v-empty-state flex flex-col items-center justify-center gap-1 py-10 text-center"
    >
        <component
            :is="icon"
            v-if="icon"
            class="mb-3 h-16 w-16 text-secondary-text"
            stroke-width="1"
            aria-hidden="true"
        />
        <h3 class="m-0 text-base font-bold text-gray-900 dark:text-gray-100">
            {{ displayTitle }}
        </h3>
        <p v-if="displaySubTitle" class="m-0 text-sm text-secondary-text">
            {{ displaySubTitle }}
        </p>
        <div v-if="hasAction" class="mt-3 flex flex-wrap items-center justify-center gap-2">
            <Link
                v-if="showCreateLink"
                :href="createRoute!"
                class="text-sm text-secondary-text hover:underline"
            >
                {{ createText }}
            </Link>
            <slot name="action" />
        </div>
    </div>

    <div v-else class="v-empty-state mt-64 flex flex-col items-center justify-center text-center">
        <component
            :is="icon"
            v-if="icon"
            class="md:1/4 h-32 w-32 text-secondary-text lg:w-1/6"
            stroke-width="1"
            aria-hidden="true"
        />
        <h3 class="pt-3 font-bold lowercase first-letter:capitalize dark:text-gray-100 lg:text-2xl">
            {{ displayTitle }}
        </h3>
        <p
            v-if="displaySubTitle"
            class="text-base lowercase text-secondary-text first-letter:capitalize"
        >
            {{ displaySubTitle }}
        </p>
        <Link
            v-if="showCreateLink"
            :href="createRoute!"
            class="text-base lowercase text-secondary-text first-letter:capitalize hover:underline"
        >
            {{ createText }}
        </Link>
        <slot name="action" />
    </div>
</template>
